import config from "../config";

/* ==========================================
   Loading Integration
   ========================================== */

let loadingHandler: ((loading: boolean) => void) | null = null;
let activeRequests = 0;

export function registerLoadingHandler(handler: (loading: boolean) => void) {
  loadingHandler = handler;
}

/* ==========================================
   Notify Integration
   ========================================== */

let notifyHandler:
  | ((type: "success" | "error" | "info", message: string) => void)
  | null = null;

export function registerNotifyHandler(
  handler: (type: "success" | "error" | "info", message: string) => void,
) {
  notifyHandler = handler;
}

/* ==========================================
   Unauthorized Handler
   ========================================== */

let unauthorizedHandler: (() => void) | null = null;
let redirectingToLogin = false;

export function registerUnauthorizedHandler(handler: () => void) {
  unauthorizedHandler = handler;
}

/* ==========================================
   Error Type
   ========================================== */

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/* ==========================================
   Types
   ========================================== */

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ApiRequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  responseType?: "blob";

  auth?: "cookie" | "bearer" | "none";

  notifySuccess?: string;
  notifyError?: boolean;

  suppressLoading?: boolean;

  authCheck?: boolean;   // 👈 new flag
}

/* ==========================================
   Helpers
   ========================================== */

function buildHeaders(opts: ApiRequestOptions): HeadersInit {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };

  if (!(opts.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (opts.auth === "bearer") {
    const token = localStorage.getItem("token");
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

/* ==========================================
   API FUNCTION
   ========================================== */

function extractApiErrorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== "object") return fallback;

  const record = body as Record<string, unknown>;
  const message =
    typeof record.message === "string"
      ? record.message
      : typeof record.error === "string"
        ? record.error
        : typeof record.title === "string"
          ? record.title
          : typeof record.detail === "string"
            ? record.detail
            : undefined;

  return message ?? fallback;
}

export async function api<T>(
  path: string,
  opts: ApiRequestOptions = {},
): Promise<T> {
  const auth = opts.auth ?? "cookie";
  const notifyError = opts.notifyError ?? true;
  const suppressLoading = opts.suppressLoading ?? false;
  const authCheck = opts.authCheck ?? false;

  try {
    if (!suppressLoading) {
      activeRequests++;
      loadingHandler?.(true);
    }

    let res: Response;

    try {
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      const normalizedBaseUrl = config.API_BASE_URL.replace(/\/+$/, "");

      res = await fetch(`${normalizedBaseUrl}${normalizedPath}`, {
        method: opts.method ?? "GET",
        credentials: auth === "none" ? "omit" : "include",
        headers: buildHeaders({ ...opts, auth }),
        body:
          opts.body === undefined
            ? undefined
            : opts.body instanceof FormData
              ? opts.body
            : typeof opts.body === "string"
              ? opts.body
              : JSON.stringify(opts.body),
        signal: opts.signal,
      });
    } catch (networkError) {
      if (notifyError) {
        notifyHandler?.("error", "Network error. Please try again.");
      }
      throw networkError;
    }

    const contentType = res.headers.get("content-type") ?? "";
    const isJson = contentType.includes("application/json");

    if (!res.ok) {
      const errBody = isJson
        ? await res.json().catch(() => null)
        : await res.text().catch(() => null);

      /* ==========================================
         Handle authentication expiry
         ========================================== */

      if (res.status === 401) {
        if (!authCheck && !redirectingToLogin) {
          redirectingToLogin = true;

          unauthorizedHandler?.();

          setTimeout(() => {
            redirectingToLogin = false;
          }, 1000);
        }
      }

      /* ==========================================
         Error notifications
         ========================================== */

      const apiMessage = extractApiErrorMessage(
        errBody,
        res.status === 403
          ? "You do not have permission to perform this action."
          : `Request failed (${res.status})`,
      );

      if (notifyError) {
        if (res.status === 403) {
          notifyHandler?.("error", apiMessage);
        } else if (!(authCheck && res.status === 401)) {
          notifyHandler?.("error", apiMessage);
        }
      }

      throw new ApiError(
        res.status,
        apiMessage,
        errBody,
      );
    }

    if (opts.notifySuccess) {
      notifyHandler?.("success", opts.notifySuccess);
    }

    if (res.status === 204) {
      return undefined as unknown as T;
    }

    return (opts.responseType === "blob" ? await res.blob() : isJson ? await res.json() : await res.text()) as T;
  } finally {
    if (!suppressLoading) {
      activeRequests--;

      if (activeRequests <= 0) {
        activeRequests = 0;
        loadingHandler?.(false);
      }
    }
  }
}

export interface WebhookDto {
  id: string;
  name: string;
  url: string;
  rateLimit: number;
  events: string[];
  sender: string[];
}

export interface UpsertWebhookDto {
  name: string;
  url: string;
  rateLimit: number;
  events: string[];
  sender: string[];
}

export function getWebhooks() {
  return api<WebhookDto[]>("/api/webhooks");
}

export function getWebhook(id: string) {
  return api<WebhookDto>(`/api/webhooks/${encodeURIComponent(id)}`);
}

export function createWebhook(webhook: UpsertWebhookDto) {
  return api<WebhookDto>("/api/webhooks", {
    method: "POST",
    body: webhook,
    notifySuccess: "Webhook created.",
  });
}

export function updateWebhook(id: string, webhook: UpsertWebhookDto) {
  return api<WebhookDto>(`/api/webhooks/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: webhook,
    notifySuccess: "Webhook updated.",
  });
}

export function deleteWebhook(id: string) {
  return api<void>(`/api/webhooks/${encodeURIComponent(id)}`, {
    method: "DELETE",
    notifySuccess: "Webhook deleted.",
  });
}

export function deleteCampaign(campaignId: number) {
  return api<void>(`/api/campaign/${campaignId}`, {
    method: "DELETE",
    notifySuccess: "Campaign deleted.",
  });
}

export function cancelCampaign(campaignId: number) {
  return api<void>(`/api/campaign/${campaignId}/cancel`, {
    method: "POST",
    notifySuccess: "Campaign cancelled.",
  });
}

export interface ConversationSummaryDto {
  conversationId: number;
  partyId?: number | null;
  partyName: string;
  channelType: string;
  remoteAddress: string;
  status: string;
  assignedUserId?: string | null;
  unreadCount: number;
  lastMessageDate: string;
  lastMessagePreview: string;
}

export interface ConversationMessageDto {
  conversationMessageId: number;
  direction: string;
  contentType: string;
  messageBody: string;
  status: string;
  trustLevel: string;
  campaignId?: number | null;
  messageDate: string;
  readDate?: string | null;
  createdByUserId?: string | null;
}

export function getConversations(unreadOnly = false, silent = false) {
  return api<ConversationSummaryDto[]>(
    `/api/conversations?status=Open&unreadOnly=${unreadOnly}`,
    {
      suppressLoading: silent,
      notifyError: !silent,
    },
  );
}

export function getConversationMessages(conversationId: number, silent = false) {
  return api<ConversationMessageDto[]>(
    `/api/conversations/${conversationId}/messages`,
    {
      suppressLoading: silent,
      notifyError: !silent,
    },
  );
}

export function markConversationRead(conversationId: number) {
  return api<void>(`/api/conversations/${conversationId}/read`, {
    method: "PUT",
    suppressLoading: true,
    notifyError: false,
  });
}

export function sendConversationReply(
  conversationId: number,
  messageBody: string,
) {
  return api<{ outboundMessageId: number }>(
    `/api/conversations/${conversationId}/messages`,
    {
      method: "POST",
      body: { messageBody },
      notifySuccess: "SMS reply queued.",
    },
  );
}

export interface ContactIssueReportDto {
  partyId: number;
  displayName: string;
  partyType: "Person" | "Family" | "Organisation" | string;
  email?: string | null;
  hasEmail: boolean;
  emailVerified: boolean;
  mobile?: string | null;
  hasMobile: boolean;
  mobileVerified: boolean;
  linkedToService: boolean;
  issues: string[];
}

export function getContactIssueReport(serviceId?: number | null) {
  const query = serviceId ? `?serviceId=${serviceId}` : "";
  return api<ContactIssueReportDto[]>(`/api/reports/contacts/issues${query}`);
}
